import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  getStakingAssetsData,
  useHandleGqlErrorsWithSnackbar,
} from '../../common';
import { debounce } from '@bts-web/utils-functions';
import { ASSETS_LIST_PAGE_SIZE } from '@bts-web/core-features/config';

export const useStakeAssetsData = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [loading, setLoading] = useState(true);

  const [rawResponse, setRawResponse] = useState<Awaited<
    ReturnType<typeof getStakingAssetsData>
  > | null>(null);

  const debouncedSetSearchTerm = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 300), // 300ms delay for search
    [],
  );

  const response = useMemo(() => {
    if (!rawResponse) return null;

    const trimmedSearchTerm = debouncedSearchTerm.trim().toLowerCase();

    if (!trimmedSearchTerm) return rawResponse;

    return {
      ...rawResponse,
      data: {
        ...rawResponse.data,
        // client-side filtering for ownedAssets since portfolio query does not support it
        ownedAssets: {
          assets: {
            edges: rawResponse.data.ownedAssets?.assets?.edges?.filter(
              (asset) =>
                asset?.node?.name?.toLowerCase().includes(trimmedSearchTerm) ||
                asset?.node?.symbol?.toLowerCase().includes(trimmedSearchTerm),
            ),
          },
        },
      },
    };
  }, [rawResponse, debouncedSearchTerm]);

  const loadMoreAssets = () => {
    fetchData(rawResponse?.data.otherAssets?.pageInfo?.endCursor, true);
  };

  const fetchData = async (
    endCursor?: string | null,
    onlyNextItems = false,
  ) => {
    if (!onlyNextItems) {
      setLoading(true);
    }

    const trimmedSearchTerm = debouncedSearchTerm.trim().toLowerCase();

    const result = await getStakingAssetsData({
      first: ASSETS_LIST_PAGE_SIZE,
      ...(trimmedSearchTerm !== '' ? { searchTerm: trimmedSearchTerm } : {}),
      ...(endCursor ? { after: endCursor } : {}),
    });

    if (result.errors) {
      processErrors(result.errors);
    }

    if (onlyNextItems) {
      setRawResponse((prev) => ({
        ...result,
        data: {
          ownedAssets: result.data.ownedAssets,
          otherAssets: {
            pageInfo: {
              endCursor: result.data.otherAssets?.pageInfo?.endCursor,
              hasNextPage:
                result.data.otherAssets?.pageInfo?.hasNextPage ?? false,
            },
            edges: [
              ...(prev?.data.otherAssets?.edges ?? []),
              ...(result.data.otherAssets?.edges ?? []),
            ],
          },
        },
      }));
    } else {
      setRawResponse(result);
    }

    setLoading(false);
  };

  useEffect(() => {
    debouncedSetSearchTerm(searchTerm);
  }, [searchTerm, debouncedSetSearchTerm]);

  const clearSearch = () => {
    setSearchTerm('');

    setDebouncedSearchTerm('');
  };

  useEffect(() => {
    if (
      debouncedSearchTerm.length > 0 &&
      debouncedSearchTerm.trim().length < 2
    ) {
      return;
    }

    fetchData();
  }, [debouncedSearchTerm]);

  return {
    loading,
    response,
    setSearchTerm,
    searchTerm: searchTerm.trim(),
    clearSearch,
    loadMoreAssets,
  };
};
